import React, { memo } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type AddPlayerTileProps = {
  onPress?: () => void;
  /**
   * `'tile'` — a vertical dashed card that fills the empty grid cell beside a lone player
   * (odd player count). `'wide'` — a full-width dashed pill below the grid (even count).
   */
  variant: 'tile' | 'wide';
};

/** "Add a player" affordance inside the populated My Kids grid (PP-376 odd/even layout). */
function AddPlayerTile({ onPress, variant }: AddPlayerTileProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const label = STRINGS.home.guardian.addPlayerEmptyCard.title;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [styles.pressable, pressed && { opacity: 0.72 }]}>
      {variant === 'tile' ? (
        <Stack flex={1} style={styles.card} align="center" justify="center" gap={scale(8)}>
          <Stack style={styles.plusCircle} align="center" justify="center">
            <SvgIcon name="IcPlusBold" size={scale(20)} color={colors.Blue} />
          </Stack>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.label}>
            {label}
          </Text>
        </Stack>
      ) : (
        <Stack
          direction="row"
          style={styles.card}
          align="center"
          justify="center"
          gap={scale(8)}
          paddingY={scale(14)}>
          <Stack style={styles.plusCircle} align="center" justify="center">
            <SvgIcon name="IcPlusBold" size={scale(18)} color={colors.Blue} />
          </Stack>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.label}>
            {label}
          </Text>
        </Stack>
      )}
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  pressable: {
    flex: 1,
  },
  card: {
    flex: 1,
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.Blue,
    paddingHorizontal: scale(12),
  },
  plusCircle: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(22),
    backgroundColor: guardianColors['Main/10'],
  },
  label: {
    color: colors.Blue,
  },
}));

export default memo(AddPlayerTile);
export type { AddPlayerTileProps };
